/**
 * TanStack-Query-Schicht: Key-Factory und Composables.
 *
 * Konvention: **jeder** Server-Zugriff der Anwendung läuft über eine der
 * Funktionen hier – Komponenten importieren nie direkt aus `patients.ts` &Co.
 * (Ausnahme: `qrImageUrl`, das nur eine URL baut, und `saveBlob`.)
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { adminApi } from './admin'
import { authApi } from './auth'
import { saveBlob } from './http'
import { licenseApi } from './license'
import { patientsApi } from './patients'
import type {
  AssignLicenseRequest,
  ChangePasswordRequest,
  CreatePatientRequest,
  CreateTherapistRequest,
  DailyCount,
  EntriesParams,
  ExportFormat,
  LicenseAssignment,
  LoginRequest,
  Patient,
  PatchTherapistRequest,
  SetPasswordRequest,
  TherapistAdmin,
  User,
} from './types'

/* ------------------------------------------------------------ Query-Keys */

export const queryKeys = {
  me: () => ['me'] as const,
  patients: () => ['patients'] as const,
  patient: (id: MaybeRefOrGetter<number>) => ['patient', toValue(id)] as const,
  entries: (id: MaybeRefOrGetter<number>, params: MaybeRefOrGetter<EntriesParams>) =>
    ['entries', toValue(id), toValue(params)] as const,
  daily: (id: MaybeRefOrGetter<number>) => ['daily', toValue(id)] as const,
  license: () => ['license'] as const,
  admin: {
    all: () => ['admin'] as const,
    therapists: () => ['admin', 'therapists'] as const,
    licenseTypes: () => ['admin', 'license-types'] as const,
    licenses: () => ['admin', 'licenses'] as const,
  },
} as const

/** Nach Login/Logout: sämtliche Serverdaten verwerfen. */
export function resetServerState(client: QueryClient): void {
  client.clear()
}

/* --------------------------------------------------------------- Queries */

/** Aktuelle Sitzung. Wird vom Router-Guard über den Store vorbefüllt. */
export function useMeQuery() {
  return useQuery({
    queryKey: queryKeys.me(),
    queryFn: () => authApi.me(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

/** Vollständige Patientenliste – die Substring-Suche filtert clientseitig. */
export function usePatientsQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: queryKeys.patients(),
    queryFn: () => patientsApi.listPatients(),
    enabled: computed(() => toValue(enabled)),
    staleTime: 30 * 1000,
  })
}

export function usePatientQuery(id: MaybeRefOrGetter<number>) {
  return useQuery({
    queryKey: computed(() => queryKeys.patient(id)),
    queryFn: () => patientsApi.getPatient(toValue(id)),
    enabled: computed(() => Number.isFinite(toValue(id)) && toValue(id) > 0),
  })
}

/**
 * Serverseitig paginierte Abfrageliste. `keepPreviousData` verhindert das
 * Flackern beim Blättern/Filtern.
 */
export function useEntriesQuery(
  id: MaybeRefOrGetter<number>,
  params: MaybeRefOrGetter<EntriesParams>,
) {
  return useQuery({
    queryKey: computed(() => queryKeys.entries(id, params)),
    queryFn: () => patientsApi.getEntries(toValue(id), toValue(params)),
    enabled: computed(() => Number.isFinite(toValue(id)) && toValue(id) > 0),
    placeholderData: keepPreviousData,
  })
}

/** Tageszähler für das Balkendiagramm „Abfragen pro Tag“. */
export function useDailyCountsQuery(
  id: MaybeRefOrGetter<number>,
  enabled: MaybeRefOrGetter<boolean> = true,
) {
  return useQuery<DailyCount[]>({
    queryKey: computed(() => queryKeys.daily(id)),
    queryFn: () => patientsApi.getDailyCounts(toValue(id)),
    enabled: computed(() => toValue(enabled) && Number.isFinite(toValue(id)) && toValue(id) > 0),
  })
}

export function useLicenseQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: queryKeys.license(),
    queryFn: () => licenseApi.getLicense(),
    enabled: computed(() => toValue(enabled)),
    staleTime: 60 * 1000,
  })
}

export function useAdminTherapistsQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: queryKeys.admin.therapists(),
    queryFn: () => adminApi.listTherapists(),
    enabled: computed(() => toValue(enabled)),
  })
}

export function useAdminLicenseTypesQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: queryKeys.admin.licenseTypes(),
    queryFn: () => adminApi.listLicenseTypes(),
    enabled: computed(() => toValue(enabled)),
  })
}

export function useAdminLicensesQuery(enabled: MaybeRefOrGetter<boolean> = true) {
  return useQuery({
    queryKey: queryKeys.admin.licenses(),
    queryFn: () => adminApi.listLicenseAssignments(),
    enabled: computed(() => toValue(enabled)),
  })
}

/* ------------------------------------------------------------- Mutations */

/** Anmeldung: Cache leeren (fremde Daten) und `me` neu setzen. */
export function useLoginMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: LoginRequest) => authApi.login(payload),
    onSuccess: (user: User) => {
      resetServerState(client)
      client.setQueryData(queryKeys.me(), user)
    },
  })
}

/** Abmeldung: Cache vollständig verwerfen. */
export function useLogoutMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      resetServerState(client)
    },
  })
}

export function useChangePasswordMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: ChangePasswordRequest) => authApi.changePassword(payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.me() })
    },
  })
}

export function useCreatePatientMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePatientRequest) => patientsApi.createPatient(payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.patients() })
      void client.invalidateQueries({ queryKey: queryKeys.license() })
    },
  })
}

/** Löschen mit optimistischer Entfernung aus der Liste. */
export function useDeletePatientMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => patientsApi.removePatient(id),
    onMutate: async (id: number) => {
      await client.cancelQueries({ queryKey: queryKeys.patients() })
      const previous = client.getQueryData<Patient[]>(queryKeys.patients())
      if (previous) {
        client.setQueryData<Patient[]>(
          queryKeys.patients(),
          previous.filter((patient) => patient.id !== id),
        )
      }
      return { previous }
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        client.setQueryData(queryKeys.patients(), context.previous)
      }
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: queryKeys.patients() })
      void client.invalidateQueries({ queryKey: queryKeys.license() })
    },
  })
}

/** Export herunterladen und direkt im Browser speichern. */
export function useExportPatientMutation() {
  return useMutation({
    mutationFn: async (variables: { id: number; format?: ExportFormat }) => {
      const file = await patientsApi.exportFile(variables.id, variables.format ?? 'xlsx')
      saveBlob(file)
      return file
    },
  })
}

export function useCreateTherapistMutation() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateTherapistRequest) => adminApi.createTherapist(payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.therapists() })
    },
  })
}

export function usePatchTherapistMutation() {
  const client = useQueryClient()
  return useMutation<TherapistAdmin, unknown, { id: number; payload: PatchTherapistRequest }>({
    mutationFn: ({ id, payload }) => adminApi.patchTherapist(id, payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.therapists() })
    },
  })
}

export function useSetTherapistPasswordMutation() {
  return useMutation<void, unknown, { id: number; payload: SetPasswordRequest }>({
    mutationFn: ({ id, payload }) => adminApi.setTherapistPassword(id, payload),
  })
}

export function useAssignLicenseMutation() {
  const client = useQueryClient()
  return useMutation<LicenseAssignment, unknown, AssignLicenseRequest>({
    mutationFn: (payload) => adminApi.assignLicense(payload),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.admin.licenses() })
      void client.invalidateQueries({ queryKey: queryKeys.admin.therapists() })
    },
  })
}
